import { useId, useState, type MouseEvent, type ReactNode } from 'react'

import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'

import { MoreHoriz } from '@mxenabled/mx-icons'

export interface OverflowMenuItem {
  /** Stable identity for the item (also its React key). */
  key: string
  /** Leading icon node. */
  icon: ReactNode
  /** Visible label. */
  label: string
  /** Invoked when the item is chosen (after the menu closes). */
  onSelect: () => void
  /** Optional per-item disabled state. */
  disabled?: boolean
}

export interface OverflowMenuProps {
  /** Items to render, top to bottom. */
  items: OverflowMenuItem[]
  /** Accessible label for the trigger button. */
  ariaLabel: string
  /** Called when the menu opens (e.g. to fire analytics). */
  onOpen?: () => void
  /** Disables the trigger entirely (e.g. an obsolete beat). */
  disabled?: boolean
}

/**
 * A "…" (MoreHoriz) overflow menu: an icon trigger that opens a list of actions.
 *
 * Presentational only — it owns nothing but the open/close anchor state. The item
 * set and what each item does are supplied by the caller via `items[].onSelect`,
 * so all data/side-effects (analytics, feedback, hide, …) live outside this file.
 */
export const OverflowMenu = ({ items, ariaLabel, onOpen, disabled = false }: OverflowMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const isOpen = Boolean(anchorEl)

  // Unique per instance so multiple menus on a page (one per card) don't share
  // DOM ids or cross-wire their aria relationships.
  const baseId = useId()
  const buttonId = `${baseId}-button`
  const menuId = `${baseId}-menu`

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    onOpen?.()
  }

  const handleClose = () => setAnchorEl(null)

  // Close before running the caller's handler so the menu never lingers over a
  // dialog/drawer the handler may open.
  const handleSelect = (onSelect: () => void) => {
    handleClose()
    onSelect()
  }

  return (
    <>
      <IconButton
        aria-controls={isOpen ? menuId : undefined}
        aria-expanded={isOpen || undefined}
        aria-haspopup="true"
        aria-label={ariaLabel}
        disabled={disabled}
        id={buttonId}
        onClick={handleOpen}
        sx={{ minHeight: 24, minWidth: 24, p: 0 }}
      >
        <MoreHoriz size={24} sx={{ color: 'neutral.dark' }} />
      </IconButton>
      <Menu
        MenuListProps={{ 'aria-labelledby': buttonId, sx: { width: 212 } }}
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        disableScrollLock={true}
        id={menuId}
        onClose={handleClose}
        open={isOpen}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        {items.map(({ key, icon, label, onSelect, disabled: itemDisabled }) => (
          <MenuItem
            disabled={itemDisabled}
            key={key}
            onClick={() => handleSelect(onSelect)}
            sx={{ p: 12 }}
          >
            <ListItemIcon sx={{ minWidth: '32px !important' }}>{icon}</ListItemIcon>
            <ListItemText
              primary={label}
              slotProps={{ primary: { fontSize: 15, fontWeight: 'normal', lineHeight: '20px' } }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default OverflowMenu
